import { Component, computed, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FaxPipe } from "@kno2/common/pipes";
import { NotificationService } from "@kno2/common/services/notifications";
import { requiredWithMessageValidator } from "@kno2/common/validators";
import { SessionService } from "@kno2/core/session";
import { AttachmentsService } from "@kno2/data-access/attachments";
import { Message, MessagesService } from "@kno2/data-access/messages";
import { UsersService } from "@kno2/data-access/users";
import { DialogService } from "@kno2/shared/dialog";
import { FormInputComponent, FormSelectComponent, FormService } from "@kno2/shared/forms";
import { NotificationAlertComponent } from "@kno2/shared/notification-alert";

@Component({
    selector: "kno2-non-phi-fax",
    imports: [FormsModule, ReactiveFormsModule, FormInputComponent, FormSelectComponent, NotificationAlertComponent],
    providers: [FaxPipe],
    templateUrl: "./non-phi-fax.component.html",
    styles: ``
})
export class NonPhiFaxComponent implements OnInit {
    private readonly usersService = inject(UsersService);
    private readonly messagesService = inject(MessagesService);
    private readonly attachmentsService = inject(AttachmentsService);
    private readonly sessionService = inject(SessionService);
    private readonly dialogService = inject(DialogService);
    private readonly formService = inject(FormService);
    private readonly notificationService = inject(NotificationService);
    private readonly faxPipe = inject(FaxPipe);
    private readonly destroyRef = inject(DestroyRef);
    private draftId: string;
    private sent = false;

    public constructor() {
        this.destroyRef.onDestroy(() => {
            if (this.draftId && !this.sent) {
                this.messagesService.deleteMessage(this.draftId);
            }
        });
    }

    protected readonly isSubmitting = signal(false);
    protected readonly isUploading = signal(false);
    protected readonly error = signal<string>(null);
    protected readonly errorMessages = signal<string[]>([]);
    protected readonly faxNumberOptions = signal<[string, string][]>([]);
    protected validAttachmentExtensions = computed(() => this.attachmentsService.fileExtensions().join(","));
    protected form = new FormGroup({
        subject: new FormControl("", requiredWithMessageValidator('"Subject" is required.')),
        toAddress: new FormControl("", requiredWithMessageValidator('"To" fax number is required.')),
        fromAddress: new FormControl(null, requiredWithMessageValidator('"From" fax number is required.')),
        attachments: new FormControl([], requiredWithMessageValidator("Missing file attachment.")),
        body: new FormControl("")
    });

    public async ngOnInit(): Promise<void> {
        const addresses = await this.usersService.getDocumentSourcesAsync(this.sessionService.getProfile().userId);
        this.faxNumberOptions.set(addresses.filter(({ type }) => type === "Fax").map(({ address }) => [this.faxPipe.transform(address), address]));
    }

    public cancel(): void {
        this.dialogService.close();
    }

    public async onFilesSelected(event: Event): Promise<void> {
        const target = event.target as HTMLInputElement;
        const files: FileList = target.files;

        if (!files.length) return;

        this.isUploading.set(true);
        if (!this.draftId) await this.createDraft();

        for (let i = 0; i < target.files.length; i++) {
            await this.addAttachment(target.files.item(i));
        }
        this.isUploading.set(false);
    }

    private async createDraft(): Promise<void> {
        const { attachments, ...message }: Partial<Message> = this.form.value;
        const draft = await this.messagesService.saveDraft(message);

        this.draftId = draft.id;
    }

    private async addAttachment(file: File): Promise<void> {
        try {
            if (file.size === 0) throw `${file.name} is empty. Please upload something more substantial.`;
            if (file.size > 20971520) throw `${file.name} exceeds the maximum (20MB) allowed size.`;

            const result = await this.messagesService.uploadAttachment(this.draftId, file);
            this.form.controls.attachments.setValue([...this.form.value.attachments, result]);
        } catch (err) {
            this.error.set("Unable to attach document.");
            if (err.error?.message) this.errorMessages.set([`${file.name}: ${err.error.message}`]);
        }
    }

    public async send(): Promise<void> {
        const errors = this.formService.getErrorValues(this.form);
        if (errors.length) {
            this.error.set("Please fix the following errors before sending.");
            this.errorMessages.set(errors as string[]);
            return;
        }
        this.isSubmitting.set(true);

        try {
            const { attachments, ...message }: Partial<Message> = this.form.value;
            await this.messagesService.sendMessage(this.draftId, message);
            this.notificationService.success("The message was sent successfully.");
            this.sent = true;
            this.dialogService.close();
        } catch (err) {
            this.error.set(err.message || "There was an error sending your message. Please contact support.");
        } finally {
            this.isSubmitting.set(false);
        }
    }
}
